import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getBrokerTools, getBrokerToolTemplate } from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import {
  getPermissionControllers,
  hasBrokerPermission,
  isBroker,
} from "../../../utils/permissionHelper";
import PermissionBrokerModelController from "../../../controller/model/PermissionBrokerModelController";
import { useToast } from "../../../contexts/ToastContext";
import "../../css/IbBrokerTools.css";

const extractBrokerToolsRows = (payload) => {
  if (!payload || typeof payload !== "object") return [];
  const d = payload.DATA ?? payload.data;
  if (Array.isArray(d)) return d;
  if (d && typeof d === "object") {
    if (Array.isArray(d.TOOLS)) return d.TOOLS;
    if (Array.isArray(d.tools)) return d.tools;
    if (Array.isArray(d.ITEMS)) return d.ITEMS;
    if (Array.isArray(d.items)) return d.items;
    if (Array.isArray(d.DATA)) return d.DATA;
    if (Array.isArray(d.data)) return d.data;
  }
  if (Array.isArray(payload.TOOLS)) return payload.TOOLS;
  if (Array.isArray(payload.tools)) return payload.tools;
  return [];
};

const pickTemplateName = (row) => {
  if (!row || typeof row !== "object") return "";
  const v =
    row.TEMPLATE_NAME ??
    row.template_name ??
    row.TEMPLATE ??
    row.template ??
    row.KEY ??
    row.key ??
    row.SLUG ??
    row.slug ??
    row.CODE ??
    row.code ??
    row.NAME ??
    row.name ??
    "";
  return String(v || "").trim();
};

const BROKER_TOOL_FILE_RE = /^broker-tool-template[\w.-]+\.(png|jpe?g|webp)$/i;

const templateNameFromSourceCode = (row) => {
  const sc = row?.SOURCE_CODE ?? row?.source_code;
  if (typeof sc !== "string") return "";
  const t = sc.trim();
  if (BROKER_TOOL_FILE_RE.test(t)) return t;
  const base = t.split(/[/\\]/).pop() || "";
  return BROKER_TOOL_FILE_RE.test(base) ? base : "";
};

const templateNameCaseInsensitive = (row) => {
  if (!row || typeof row !== "object") return "";
  for (const k of Object.keys(row)) {
    if (String(k).toLowerCase() === "template") {
      const v = row[k];
      if (v != null && String(v).trim() !== "") return String(v).trim();
    }
  }
  return "";
};

const originalTemplateName = (row) => {
  const fromTemplate = pickTemplateName(row) || templateNameCaseInsensitive(row);
  if (fromTemplate) return fromTemplate;
  return templateNameFromSourceCode(row);
};

const CARD_GRADIENTS = [
  "linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)",
  "linear-gradient(135deg, #10b981 0%, #14b8a6 100%)",
  "linear-gradient(135deg, #f97316 0%, #ef4444 100%)",
  "linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)",
  "linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)",
  "linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)",
  "linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)",
  "linear-gradient(135deg, #64748b 0%, #475569 100%)",
];

const CARD_ICONS = [
  "fa-id-card",
  "fa-file-lines",
  "fa-utensils",
  "fa-share-nodes",
  "fa-qrcode",
  "fa-store",
  "fa-calendar-days",
  "fa-image",
];

const normalizeBrokerTool = (row, index) => {
  const templateName = originalTemplateName(row);
  const id = row.ID ?? row.id ?? (templateName || `tool-${index}`);
  const title =
    row.TITLE ??
    row.title ??
    row.NAME ??
    row.name ??
    row.LABEL ??
    row.label ??
    (templateName || `Tool ${index + 1}`);
  const subtitle =
    row.SUBTITLE ??
    row.subtitle ??
    row.DESCRIPTION ??
    row.description ??
    row.AUTHOR ??
    row.author ??
    row.VENDOR ??
    row.vendor ??
    row.PROVIDER ??
    row.provider ??
    row.CATEGORY ??
    row.category ??
    "";
  const thumbUrl =
    row.THUMBNAIL_URL ??
    row.thumbnail_url ??
    row.PREVIEW_URL ??
    row.preview_url ??
    row.IMAGE_URL ??
    row.image_url ??
    row.IMAGE ??
    row.image ??
    null;

  return {
    id: String(id),
    templateName,
    title: String(title).trim(),
    subtitle: String(subtitle || "").trim(),
    thumbUrl: thumbUrl ? String(thumbUrl).trim() : null,
    raw: row,
  };
};

const inferMediaFromBlob = (blob) => {
  const ct = String(blob?.type || "").toLowerCase();
  if (ct.startsWith("video/")) return "video";
  if (ct.startsWith("image/")) return "image";
  return "unknown";
};

/** Broker tools grid — used on IB page and embedded in Marketing Tools. */
export default function BrokerToolsPanel({ user: userProp = null, showHeader = false }) {
  const { t } = useTranslation("mobile");
  const navigate = useNavigate();
  const { error: showError } = useToast();
  const [user, setUser] = useState(userProp);
  const [loadingUser, setLoadingUser] = useState(!userProp);
  const [loadingTools, setLoadingTools] = useState(true);
  const [tools, setTools] = useState([]);
  const previewBlobUrlsRef = useRef([]);

  useEffect(() => {
    if (userProp) {
      setUser(userProp);
      setLoadingUser(false);
      return;
    }
    try {
      const stored = sessionStorage.getItem("userData");
      if (stored) setUser(JSON.parse(stored));
    } catch (_) {
      setUser(null);
    } finally {
      setLoadingUser(false);
    }
  }, [userProp]);

  const permissions = useMemo(() => getPermissionControllers(user), [user]);
  const { brokerPerm, brokerDevicePerm } = permissions;

  const canViewBrokerTools = useMemo(() => {
    if (!isBroker(user)) return false;
    return hasBrokerPermission(
      brokerPerm,
      brokerDevicePerm,
      PermissionBrokerModelController.PER__CAN_VIEW_BROKER_TOOLS
    );
  }, [user, brokerPerm, brokerDevicePerm]);

  const loadPreviewBlob = useCallback(async (templateName) => {
    const res = await getBrokerToolTemplate(templateName, { original: true });
    const blob = res?.data;
    if (!blob || !(blob instanceof Blob)) return null;
    const ct = String(blob.type || "").toLowerCase();
    if (ct.includes("application/json") || ct.includes("text/html")) {
      return null;
    }
    const media = inferMediaFromBlob(blob);
    if (media === "unknown") return null;
    const url = URL.createObjectURL(blob);
    return { url, media };
  }, []);

  useEffect(() => {
    if (loadingUser) return;
    if (!canViewBrokerTools) {
      setLoadingTools(false);
      setTools([]);
      return;
    }

    let cancelled = false;
    previewBlobUrlsRef.current = [];

    (async () => {
      try {
        setLoadingTools(true);
        const response = await getBrokerTools();
        if (cancelled) return;
        const payload = response?.decryptedData || response?.data || {};
        if (Number(payload?.CODE) !== AppConstants.responseOk) {
          showError(
            "Error Occurred",
            payload?.MESSAGE || payload?.message || t("no_data_found", { defaultValue: "No data found" })
          );
          setTools([]);
          return;
        }

        const rows = extractBrokerToolsRows(payload);
        const normalized = rows.map((row, i) => normalizeBrokerTool(row, i));

        const withPreviews = await Promise.all(
          normalized.map(async (tool) => {
            if (tool.thumbUrl) {
              return { ...tool, previewUrl: tool.thumbUrl, previewMedia: "image", previewLoading: false };
            }
            if (!tool.templateName) {
              return { ...tool, previewUrl: null, previewMedia: null, previewLoading: false };
            }
            try {
              const preview = await loadPreviewBlob(tool.templateName);
              if (preview?.url) {
                previewBlobUrlsRef.current.push(preview.url);
                return {
                  ...tool,
                  previewUrl: preview.url,
                  previewMedia: preview.media,
                  previewLoading: false,
                };
              }
            } catch (_) {
              /* ignore per-card preview errors */
            }
            return { ...tool, previewUrl: null, previewMedia: null, previewLoading: false };
          })
        );

        if (!cancelled) setTools(withPreviews);
      } catch (err) {
        if (!cancelled) {
          showError("Error Occurred", err?.message || "Failed to load broker tools.");
          setTools([]);
        }
      } finally {
        if (!cancelled) setLoadingTools(false);
      }
    })();

    return () => {
      cancelled = true;
      previewBlobUrlsRef.current.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch (_) {}
      });
      previewBlobUrlsRef.current = [];
    };
  }, [loadingUser, canViewBrokerTools, loadPreviewBlob, showError, t]);

  return (
    <div className="ib-broker-tools-panel">
      {showHeader && (
        <header className="ib-broker-tools-head">
          <h2 className="ib-broker-tools-title">{t("ib_broker_tools", { defaultValue: "Broker tools" })}</h2>
          <p className="ib-broker-tools-sub">
            {t("ib_broker_tools_sub", {
              defaultValue: "Templates and resources available for your brokerage.",
            })}
          </p>
        </header>
      )}

      {!loadingUser && !canViewBrokerTools && (
        <div className="ib-broker-tools-denied" role="alert">
          {t("ib_broker_tools_no_permission", {
            defaultValue: "You do not have permission to view broker tools.",
          })}
        </div>
      )}

      {canViewBrokerTools && loadingTools && (
        <div className="ib-broker-tools-grid" aria-busy="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={`sk-${i}`} className="ib-broker-tools-card">
              <div className="ib-broker-tools-card-media ib-broker-tools-card-media--loading" />
              <div className="ib-broker-tools-card-body">
                <span className="ib-dashboard-skeleton ib-dashboard-skeleton--name" />
                <span className="ib-dashboard-skeleton ib-dashboard-skeleton--sub" style={{ marginTop: 8 }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {canViewBrokerTools && !loadingTools && tools.length === 0 && (
        <div className="ib-broker-tools-empty">{t("no_data_found", { defaultValue: "No data found" })}</div>
      )}

      {canViewBrokerTools && !loadingTools && tools.length > 0 && (
        <div className="ib-broker-tools-grid">
          {tools.map((tool, index) => {
            const canOpenEditor = Boolean(tool.templateName);
            const openEditor = () => {
              if (!tool.templateName) return;
              navigate(
                `/pages/ibDashboard/broker-tools/editor?template=${encodeURIComponent(
                  tool.templateName
                )}&title=${encodeURIComponent(tool.title || tool.templateName)}&toolId=${encodeURIComponent(tool.id)}`
              );
            };
            const gradient = CARD_GRADIENTS[index % CARD_GRADIENTS.length];
            const iconClass = CARD_ICONS[index % CARD_ICONS.length];
            return (
              <article key={tool.id} className="ib-broker-tools-card">
                <div
                  className="ib-broker-tools-card-media"
                  style={!tool.previewUrl ? { background: gradient } : undefined}
                >
                  {tool.previewUrl ? (
                    tool.previewMedia === "video" ? (
                      <video src={tool.previewUrl} muted playsInline preload="metadata" />
                    ) : (
                      <img src={tool.previewUrl} alt="" loading="lazy" />
                    )
                  ) : (
                    <div className="ib-broker-tools-card-media-icon" aria-hidden>
                      <i className={`fa-solid ${iconClass}`} />
                    </div>
                  )}
                  {canOpenEditor ? (
                    <div className="ib-broker-tools-card-media-overlay">
                      <button
                        type="button"
                        className="ib-broker-tools-card-preview-eye"
                        onClick={openEditor}
                        aria-label={t("ib_broker_tools_open_editor", {
                          defaultValue: "Open {{title}}",
                          title: tool.title,
                        })}
                      >
                        <i className="fa-solid fa-eye" aria-hidden />
                      </button>
                    </div>
                  ) : null}
                </div>
                <div className="ib-broker-tools-card-body">
                  <h2 className="ib-broker-tools-card-title" title={tool.title}>
                    {tool.title}
                  </h2>
                  {tool.subtitle ? (
                    <p className="ib-broker-tools-card-desc" title={tool.subtitle}>
                      {tool.subtitle}
                    </p>
                  ) : null}
                  {canOpenEditor ? (
                    <button type="button" className="ib-broker-tools-card-cta" onClick={openEditor}>
                      {t("ib_broker_tools_customize", { defaultValue: "Customize & download" })}
                    </button>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
